import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { ImageUpload } from "@/components/ImageUpload";
import { CompanyParams } from "@/components/sistema/CompanyParams";
import { api, errMsg, maskDoc, maskPhone, maskCep, digits, canSee } from "@/lib/api";
import { validDoc } from "@/lib/document";
import { useAuth } from "@/context/AuthContext";
import { CertificadoDigital, BuscaCnpjEmpresa } from "@/fiscal";
import { toast } from "sonner";

const REGIMES = ["Simples Nacional", "Simples Nacional - MEI", "Lucro Presumido", "Lucro Real"];
const REGIME_SUGERIDO = { MEI: "Simples Nacional - MEI", "Simples Nacional": "Simples Nacional" };
const fillEmpty = (cur = {}, inc) => ({ ...cur, ...Object.fromEntries(Object.entries(inc).filter(([k, v]) => v && !cur[k])) });

const preencherVazios = (x, d) => {
  const { municipio, ...end } = d.endereco || {};
  return {
    ...fillEmpty(x, { razao_social: d.razao_social, name: d.nome_fantasia, ie: d.inscricao_estadual, email: (d.email || "").toLowerCase(), phone: digits(d.telefone || ""), cnae: digits(d.cnae_principal || "") }),
    regime_tributario: x.regime_tributario || REGIME_SUGERIDO[d.regime_sugerido] || "",
    endereco: fillEmpty(x.endereco, { ...end, cep: digits(end.cep || ""), cidade: municipio }),
  };
};
const paraModulo = (e = {}) => ({ ...e, municipio: e.cidade });
const doModulo = (end) => ({ cep: digits(end.cep || ""), logradouro: end.logradouro, numero: end.numero, complemento: end.complemento, bairro: end.bairro,
  cidade: end.municipio, uf: end.uf, codigo_municipio: end.codigo_municipio });

function CertTab() {
  const { session } = useAuth();
  if (!canSee(session?.user, "fiscal")) {
    return <p className="text-sm text-muted-foreground" data-testid="company-cert-locked">O certificado digital é usado pelo módulo Fiscal, disponível para Dono, Administrador e Gerente nos planos com o módulo Fiscal.</p>;
  }
  return <div className="rounded-xl bg-white p-4 text-gray-900" data-testid="company-cert-panel"><CertificadoDigital titulo="Certificado digital (e-CNPJ A1)" /></div>;
}

const EMPTY = { name: "", razao_social: "", documento: "", ie: "", im: "", regime_tributario: "Simples Nacional", cnae: "", endereco: {}, phone: "", whatsapp: "", email: "", site: "", logo_url: "", order_text: "", active: true };

function MainTab({ f, setF, isMain, fiscalOn }) {
  const [cnaeDica, setCnaeDica] = useState(null);
  const set = (k, mask) => (e) => setF((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  const addr = (k, mask) => (e) => setF((x) => ({ ...x, endereco: { ...x.endereco, [k]: mask ? mask(e.target.value) : e.target.value } }));
  const lookupCep = async () => {
    const cep = digits(f.endereco.cep);
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then((x) => x.json());
      if (!r.erro) setF((x) => ({ ...x, endereco: { ...x.endereco, logradouro: r.logradouro, bairro: r.bairro, cidade: r.localidade, uf: r.uf, codigo_municipio: r.ibge || x.endereco.codigo_municipio } }));
    } catch { /* busca opcional */ }
  };
  const e = f.endereco || {};
  return (
    <div className="grid sm:grid-cols-4 gap-4">
      <div className="sm:col-span-4"><p className="text-sm font-medium mb-2">Logo da empresa</p><ImageUpload value={f.logo_url} onChange={(v) => setF((x) => ({ ...x, logo_url: v }))} /></div>
      <Field label="CPF / CNPJ" hint={isMain ? "Para trocar CPF por CNPJ, peça ao suporte" : ""}><Input value={maskDoc(f.documento)} disabled={isMain} onChange={set("documento", digits)} data-testid="company-doc-input" /></Field>
      <Field label="Razão social" className="sm:col-span-3"><Input value={f.razao_social} onChange={set("razao_social")} data-testid="company-razao-input" /></Field>
      {fiscalOn && (
        <div className="sm:col-span-4 -mt-2" data-testid="company-cnpj-lookup">
          <BuscaCnpjEmpresa cnpj={f.documento} inscricaoEstadual={f.ie} enderecoAtual={paraModulo(f.endereco)}
            onPreencher={(d) => { setCnaeDica(d.cnae_principal ? { codigo: digits(d.cnae_principal), texto: d.cnae_descricao } : null); setF((x) => preencherVazios(x, d)); }}
            onSubstituirEndereco={(end) => setF((x) => ({ ...x, endereco: doModulo(end) }))}
            onUsarIsento={() => setF((x) => ({ ...x, ie: "ISENTO" }))} />
        </div>
      )}
      <Field label="Nome fantasia" className="sm:col-span-2"><Input value={f.name} onChange={set("name")} data-testid="company-name-input" /></Field>
      <Field label="Inscrição estadual" hint="Sem IE? Digite ISENTO"><Input value={f.ie} onChange={set("ie")} data-testid="company-ie-input" /></Field>
      <Field label="Inscrição municipal"><Input value={f.im} onChange={set("im")} data-testid="company-im-input" /></Field>
      <Field label="Regime tributário" className="sm:col-span-2">
        <Select value={f.regime_tributario || ""} onValueChange={(v) => setF((x) => ({ ...x, regime_tributario: v }))}>
          <SelectTrigger data-testid="company-regime-select"><SelectValue placeholder="Selecione o regime" /></SelectTrigger>
          <SelectContent>{REGIMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="CNAE principal" className="sm:col-span-2" hint={cnaeDica?.texto && cnaeDica.codigo === f.cnae ? `Receita: ${cnaeDica.texto}` : ""}><Input value={f.cnae} onChange={set("cnae", digits)} data-testid="company-cnae-input" /></Field>
      <Field label="CEP"><Input value={maskCep(e.cep || "")} onChange={addr("cep", maskCep)} onBlur={lookupCep} data-testid="company-cep-input" /></Field>
      <Field label="Endereço" className="sm:col-span-2"><Input value={e.logradouro || ""} onChange={addr("logradouro")} data-testid="company-street-input" /></Field>
      <Field label="Número"><Input value={e.numero || ""} onChange={addr("numero")} data-testid="company-number-input" /></Field>
      <Field label="Complemento"><Input value={e.complemento || ""} onChange={addr("complemento")} /></Field>
      <Field label="Bairro"><Input value={e.bairro || ""} onChange={addr("bairro")} data-testid="company-district-input" /></Field>
      <Field label="Cidade"><Input value={e.cidade || ""} onChange={addr("cidade")} data-testid="company-city-input" /></Field>
      <Field label="UF"><Input value={e.uf || ""} maxLength={2} onChange={addr("uf", (v) => v.toUpperCase())} data-testid="company-uf-input" /></Field>
      <Field label="Código IBGE do município" hint="7 dígitos · vem da busca do CEP ou do CNPJ"><Input value={e.codigo_municipio || ""} inputMode="numeric" maxLength={7} onChange={addr("codigo_municipio", (v) => digits(v).slice(0, 7))} data-testid="company-ibge-input" /></Field>
      <Field label="Telefone"><Input value={maskPhone(f.phone)} onChange={set("phone", digits)} data-testid="company-phone-input" /></Field>
      <Field label="WhatsApp"><Input value={maskPhone(f.whatsapp)} onChange={set("whatsapp", digits)} data-testid="company-whatsapp-input" /></Field>
      <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} data-testid="company-email-input" /></Field>
      <Field label="Site"><Input value={f.site} onChange={set("site")} placeholder="www.suaempresa.com.br" data-testid="company-site-input" /></Field>
      {!isMain && <label className="flex items-center gap-3 text-sm sm:col-span-4"><Switch checked={f.active} onCheckedChange={(v) => setF((x) => ({ ...x, active: v }))} data-testid="company-active-switch" />Empresa ativa</label>}
    </div>
  );
}

export function CompanyDialog({ company, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { session } = useAuth();
  const fiscalOn = canSee(session?.user, "fiscal");
  useEffect(() => { if (company) setF({ ...EMPTY, ...company, endereco: { ...(company.endereco || {}) } }); }, [company]);
  const isMain = !!company?.is_main;
  const save = async () => {
    const doc = digits(f.documento);
    if (!isMain && doc && !validDoc(doc)) return toast.error("CPF ou CNPJ inválido: confira os dígitos");
    const ibge = digits(f.endereco?.codigo_municipio || "");
    if (ibge && ibge.length !== 7) return toast.error("O código IBGE do município tem 7 dígitos");
    setBusy(true);
    try {
      const body = { ...f, documento: digits(f.documento), endereco: { ...f.endereco, cep: digits(f.endereco.cep) } };
      company.id ? await api.put(`/stores/${company.id}`, body) : await api.post("/stores", body);
      toast.success("Empresa salva");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!company} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="company-dialog">
        <DialogHeader><DialogTitle>{company?.id ? `Editar empresa${isMain ? " (principal)" : ""}` : "Nova empresa / filial"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="principal">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="principal" data-testid="company-tab-principal">Principal</TabsTrigger>
            <TabsTrigger value="parametros" data-testid="company-tab-parametros">Parâmetros</TabsTrigger>
            <TabsTrigger value="texto" data-testid="company-tab-texto">Texto padrão do pedido</TabsTrigger>
            {isMain && <TabsTrigger value="certificado" data-testid="company-tab-certificado">Certificado digital</TabsTrigger>}
          </TabsList>
          {isMain && <TabsContent value="certificado" className="mt-4"><CertTab /></TabsContent>}
          <TabsContent value="principal" className="mt-4"><MainTab f={f} setF={setF} isMain={isMain} fiscalOn={fiscalOn} /></TabsContent>
          <TabsContent value="parametros" className="mt-4">
            {isMain ? <CompanyParams /> : <p className="text-sm text-muted-foreground" data-testid="company-params-note">Os parâmetros (taxas, estoque e fidelidade) valem para todas as unidades e são definidos na empresa principal.</p>}
          </TabsContent>
          <TabsContent value="texto" className="mt-4 grid md:grid-cols-2 gap-4">
            <Field label="Mensagem impressa no rodapé dos pedidos e comprovantes"><Textarea rows={8} value={f.order_text} onChange={(e) => setF((x) => ({ ...x, order_text: e.target.value }))} placeholder="Ex.: Obrigado pela preferência! Trocas em até 7 dias com este comprovante." data-testid="company-order-text-input" /></Field>
            <div className="rounded-lg bg-white p-4 font-mono text-xs text-slate-900 self-start" data-testid="company-order-text-preview">
              <p className="text-center font-bold">{f.name || "Sua empresa"}</p>
              <p className="border-t border-dashed border-slate-400 my-2" />
              <p>1x Produto exemplo ........ R$ 10,00</p>
              <p className="border-t border-dashed border-slate-400 my-2" />
              <p className="text-center whitespace-pre-line">{f.order_text || "(sem mensagem)"}</p>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={save} disabled={busy} data-testid="company-save-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvar empresa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
